import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { superficie } from '@anery/design-system';
import { useEffect, useMemo, useRef, useState } from 'react';

import { formatarDataHora } from '../../../shared/dominio/datas';
import { documentoDoCorpo, SANDBOX_DO_CORPO } from './documentoDoCorpo';
import { lerEml } from './lerEml';
import {
  CLASSE_DO_TRECHO,
  limparTrechos,
  marcarTrechos,
  type BuscaNoEmail,
  type TrechosMarcados,
} from './marcarTrecho';

/** O que a busca achou, sem as marcas: é o que a tela conta para quem confere. */
export type ResultadoNoEmail = Pick<TrechosMarcados, 'ocorrencias' | 'pedacos'>;

/**
 * Quanto do `.eml` entra no bloco de texto cru. Um e-mail real chega a centenas de KB (anexos em base64) e
 * dentro de um `<pre>` isso trava a rolagem; o arquivo inteiro continua no botão de download.
 */
const LIMITE_DA_PREVIA_DO_EML = 200_000;

const ALTURA_DO_CORPO = 480;

/** Leva o primeiro trecho para o meio da área visível SÓ do corpo do e-mail, sem rolar a página. */
function rolarAte(marca: HTMLElement, recipiente: HTMLElement | Window): void {
  const topoDaMarca = marca.getBoundingClientRect().top;
  if (recipiente instanceof HTMLElement) {
    const topoDoRecipiente = recipiente.getBoundingClientRect().top;
    recipiente.scrollTop += topoDaMarca - topoDoRecipiente - recipiente.clientHeight / 2;
    return;
  }
  recipiente.scrollBy(0, topoDaMarca - recipiente.innerHeight / 2);
}

function CorpoEmHtml({
  html,
  imagensPorCid,
  busca,
  aoMarcar,
}: {
  html: string;
  imagensPorCid: ReadonlyMap<string, string>;
  busca: BuscaNoEmail | null;
  aoMarcar: (resultado: ResultadoNoEmail | null) => void;
}): React.JSX.Element {
  const documento = useMemo(() => documentoDoCorpo(html, imagensPorCid), [html, imagensPorCid]);
  const quadro = useRef<HTMLIFrameElement>(null);
  // O `load` do `srcdoc` pode chegar antes do React ouvir, e o primeiro documento do quadro é um `about:blank`
  // vazio: o corpo é lido do quadro na hora de marcar, e cada `load` só pede para marcar de novo.
  const [carregamentos, definirCarregamentos] = useState(0);

  useEffect(() => {
    const corpoDoEmail = quadro.current?.contentDocument?.body ?? null;
    if (corpoDoEmail === null) return;
    if (busca === null) {
      limparTrechos(corpoDoEmail);
      aoMarcar(null);
      return;
    }
    const { marcas, ocorrencias, pedacos } = marcarTrechos(corpoDoEmail, busca);
    const janela = corpoDoEmail.ownerDocument.defaultView;
    const primeira = marcas[0];
    if (primeira !== undefined && janela !== null) rolarAte(primeira, janela);
    aoMarcar({ ocorrencias, pedacos });
  }, [carregamentos, documento, busca, aoMarcar]);

  return (
    <Box
      ref={quadro}
      component="iframe"
      title="Corpo do e-mail"
      sandbox={SANDBOX_DO_CORPO}
      srcDoc={documento}
      onLoad={() => {
        definirCarregamentos((atual) => atual + 1);
      }}
      sx={{
        width: '100%',
        height: ALTURA_DO_CORPO,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: superficie.cartao,
      }}
    />
  );
}

function CorpoEmTexto({
  texto,
  busca,
  aoMarcar,
}: {
  texto: string;
  busca: BuscaNoEmail | null;
  aoMarcar: (resultado: ResultadoNoEmail | null) => void;
}): React.JSX.Element {
  // O texto entra pelo DOM, não como filho do React: as marcas mexem nos nós, e o React não pode disputar eles.
  const recipiente = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const elemento = recipiente.current;
    if (elemento === null) return;
    elemento.textContent = texto;
    if (busca === null) {
      aoMarcar(null);
      return;
    }
    const { marcas, ocorrencias, pedacos } = marcarTrechos(elemento, busca);
    const primeira = marcas[0];
    if (primeira !== undefined) rolarAte(primeira, elemento);
    aoMarcar({ ocorrencias, pedacos });
  }, [texto, busca, aoMarcar]);

  return (
    <Box
      ref={recipiente}
      sx={{
        p: 1.5,
        maxHeight: ALTURA_DO_CORPO,
        overflow: 'auto',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: '0.875rem',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        [`& mark.${CLASSE_DO_TRECHO}`]: {
          bgcolor: superficie.realce,
          color: 'inherit',
          borderRadius: '2px',
          boxShadow: `0 0 0 2px ${superficie.realce}`,
        },
      }}
    />
  );
}

function LinhaDoCabecalho({ rotulo, valor }: { rotulo: string; valor: string | null }): React.JSX.Element | null {
  if (valor === null) return null;
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
        {rotulo}
      </Typography>
      <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
        {valor}
      </Typography>
    </Stack>
  );
}

function dataLegivel(data: string | null): string | null {
  if (data === null) return null;
  const instante = new Date(data);
  return Number.isNaN(instante.getTime()) ? data : formatarDataHora(instante);
}

/**
 * O e-mail como um leitor de e-mail mostra: quem mandou, quando, o assunto e o corpo montado, com as imagens
 * embutidas no lugar. Com `busca`, marca no corpo de onde o valor do campo saiu e conta as vezes em `aoMarcar`
 * (`null` quando não há busca). O `.eml` cru continua a um clique, para quem precisa dele.
 */
export function EmailLegivel({
  eml,
  busca,
  aoMarcar,
}: {
  eml: string;
  busca: BuscaNoEmail | null;
  aoMarcar: (resultado: ResultadoNoEmail | null) => void;
}): React.JSX.Element {
  const lido = useMemo(() => lerEml(eml), [eml]);
  const [verCru, definirVerCru] = useState(false);

  useEffect(() => {
    if (verCru) aoMarcar(null);
  }, [verCru, aoMarcar]);

  return (
    <Stack spacing={1}>
      <Stack spacing={0.25}>
        <LinhaDoCabecalho rotulo="De" valor={lido.de} />
        <LinhaDoCabecalho rotulo="Para" valor={lido.para} />
        <LinhaDoCabecalho rotulo="Data" valor={dataLegivel(lido.data)} />
        <LinhaDoCabecalho rotulo="Assunto" valor={lido.assunto} />
      </Stack>

      {verCru ? (
        <>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              minHeight: 240,
              maxHeight: ALTURA_DO_CORPO,
              overflow: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {eml.slice(0, LIMITE_DA_PREVIA_DO_EML)}
          </Box>
          {eml.length > LIMITE_DA_PREVIA_DO_EML ? (
            <Typography variant="caption" color="text.secondary" role="note">
              Mostrando só o começo do e-mail (os primeiros {LIMITE_DA_PREVIA_DO_EML.toLocaleString('pt-BR')}{' '}
              caracteres). Baixe o .eml para ver o restante.
            </Typography>
          ) : null}
        </>
      ) : lido.html !== null ? (
        <CorpoEmHtml html={lido.html} imagensPorCid={lido.imagensPorCid} busca={busca} aoMarcar={aoMarcar} />
      ) : lido.texto !== null && lido.texto.trim() !== '' ? (
        <CorpoEmTexto texto={lido.texto} busca={busca} aoMarcar={aoMarcar} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          Este e-mail não tem texto para mostrar. Veja os anexos ou o .eml cru.
        </Typography>
      )}

      <Button
        size="small"
        sx={{ alignSelf: 'start' }}
        onClick={() => {
          definirVerCru((atual) => !atual);
        }}
      >
        {verCru ? 'Ver o e-mail montado' : 'Ver o .eml cru'}
      </Button>
    </Stack>
  );
}
